/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts" />
/**
 * ITestHubRefreshAwareState: This state contains global information for the test plans hub like testPlanId, testPlanName, rootSuiteId etc.
 * Information contained here should be useful to all other components, this can be considered as the base data info for the test plans page.
 * All other suite and pivot specific state extend from this 'global' state.
 */
export interface ITestHubRefreshAwareState {
    testHubRefreshState: ITestHubRefreshState;
}

export interface ITestHubRefreshState {
    testPlanId: number;
    testPlanName: string;
    testPlanIteration: string;
    testPlanAreaPath: string;
    testPlanStartDate: Date;
    testPlanEndDate: Date;
    rootSuiteId: number;
    // This gets changed as we select different suite
    selectedSuiteId: number;
    // This is the selected suite at the load of the page and is never changed
    initialSelectedSuiteId: number;
    selectedSuiteName: string;
    isAdvancedExtensionEnabled: boolean;
    // This indicates if the items are still loading and spinner neds to be shown
    isLoading: boolean;
    // This indicates that the view is busy due to some user operation
    isBusy: boolean;
    // This is to indicates if plan along with suites and cases/points are reloaded. Needed for selection of the suites.
    isReload: boolean;
    testCasePageSize: number;
    testPointPageSize: number;
    // This indicates if the current view in Author tab is grid view or list view.
    isInGridView: boolean;
    // This indicates if paging is in progress
    isPagingInProgress: boolean;
    // This indicates if the grid has unsaved changes.
    isGridDirty: boolean;
    // These are the additional columns selected for the define tab.
    defineColumnOptionFields: string[];
    // These are the additional columns selected for the execute tab.
    executeColumnOptionFields: string[];
    // These are column widths for define tab
    defineColumnAndWidthMap: { [columnId: string]: number };
    // These are column widths for execute tab
    executeColumnAndWidthMap: { [columnId: string]: number };
    // Width of splitter that separates suites tree on the left pane and the define/execute pivots on the right pane
    splitterWidth: number;
    // status of the test case preview panel (off/right)
    testCasePreviewStatus: string;
    // BuildId selected in test plan settings
    selectedBuildId: string;
    // ReleaseId selected in test plan settings
    selectedReleaseId: number;
    // ReleaseEnvironmentId selected in test plan settings
    selectedReleaseEnvironmentId: number;
    // BuildDefnId selected in test plan settings
    selectedBuildDefnId: string;
    // This indicates if show tests from child suites option is selected or not
    isShowTestsFromChildSuitesEnabled: boolean;
    // Url to all test plans page, used in breadcrumb service
    mineAllPageUrl: string;
    // mapping of define tab custom column reference name and display name
    defineTabCustomColumnFieldMap: { [columnId: string]: string };
    // mapping of execute tab custom column reference name and display name
    executeTabCustomColumnFieldMap: { [columnId: string]: string };
}

export const enum CloneOperationState {
    Failed = 2,
    InProgress = 1,
    Queued = 0,
    Succeeded = 3
}

export interface ITestHubFilterAwareState {
    /**
     * AuthorFilterReducer manages this node in the state
     */
    authorFilterState?: ITestHubAuthorFilterState;

    /**
     * PointsFilterReducer manages this node in the state
     */
    executeFilterState?: ITestHubExecuteFilterState;
}

export interface ITestHubAuthorFilterState {
    filteredTestCaseIds?: number[];
}

export interface ITestHubExecuteFilterState {
    filteredTestPointIds?: number[];
}

export interface IUserIdentity {
    displayName: string;
    id: string;
}

/**
 * ITestHubRefreshLocalContext: Contract for the react.context object that we use to store data that doesn't belong in redux store.
 * How do you decide what data to store in the context:
 * - When putting it in the redux store does not make sense. Changing of data doesn't really require you to re-render the components
 * - When you want to access same data across various components/bundles without using prop drilling
 * - Use this to store trasient and mutable data
 */
export interface ITestHubRefreshLocalContext {
    /**
     * _isPageLoad: This will be true while is page loading and after one of the pivots has been loaded, it will be set to false.
     * This is used in logging page load TTI telemetry data correctly.
     */
    _isPageLoad: boolean;
    isPageLoad(): boolean;
    setPageLoad(value: boolean): void;

    /**
     * _currentSelectedPivot: This stores the value of the currently selected pivot. Used in various components.
     * For e.g. Suite count related computations in SuiteComponent/SuiteSaga need to be performed only if the currently selected tab is Execute
     */
    _currentSelectedPivot: string | undefined;
    getCurrentPivot(): string | undefined;
    setCurrentPivot(val: string): void;

    /**
     * _webRunnerPointIds: list of point Ids which are opened in the latest web runner. In case of multiple web runner, it will have point Ids only for the latest web runner
     * This is used to refresh these point ids when the web runner is closed.
     */
    _webRunnerPointIds: number[];
    getWebRunnerPointIds(): number[];
    setWebRunnerPointIds(Ids: number[]): void;

    /**
     * _selectedTestCaseIds: list of test case Ids which were selected in the define tab once the drag test case operation has started. These test case ids are required by the suiteTree component
     * when test cases are dropped on suite since the test case table selection is not present in the suite component.
     */
    _selectedTestCaseIds: number[];
    getSelectedTestCaseIds(): number[];
    setSelectedTestCaseIds(Ids: number[]): void;

    /**
     * _isJsonIslandDataDirty: Denotes if the json island data state is dirty or not. Here's the scenario this tries to fix: you load the page in Define tab and change the title of the test case.
     * Now you switch to execute tab, and switch back to Define tab. In the switch back when the AuthorModule (define tab) loads, it finds out of date data in json island and uses that to render
     * the page. This renders data which is stale.
     * The boolean here keeps track of dirty state and marks the json island data to be dirty if you modify a test case, add a new test case, update the outcome of a point etc.
     * If the state of json island is dirty, we load the data back from the server and ignore the json island data.
     */
    _isJsonIslandDataDirty: boolean;
    isJsonIslandDataDirty(): boolean;
    makeJsonIslandDataDirty(): void;

    /**
     * _isAuthorFilterActiveOrSet: Denotes if test cases on define tab are filtered, we show count as (x of y) in this case
     */
    _isAuthorFilterActiveOrSet: boolean;
    isAuthorFilterActiveOrSet(): boolean;
    setIsAuthorFilterActiveOrSet(value: boolean): void;

    /**
     * _isExecuteFilterActiveOrSet: Denotes if test points on execute tab are filtered, we show count as (x of y) in this case
     */
    _isExecuteFilterActiveOrSet: boolean;
    isExecuteFilterActiveOrSet(): boolean;
    setIsExecuteFilterActiveOrSet(value: boolean): void;
}
